<template>
  <fts-profile
    :user-name="sender"
    fallback-name="Inistate"
    :profile-photo-size="30"
    profile-name-class="notification-content-details"
    class="fts-notification-content notification-content"
    :class="{'notification-content--unread': !notification.read, 'notification-content--rich': isContent}"
    :key="notification.id"
    @click.native="select"
  >
    <div
      slot="profileName"
      slot-scope="{ userProfileName }"
    >
      <!-- Content notification (docs/notification-content-proposal.md §8.1): title, plain
           summary, source label. No v-html — the summary is plain text — and no counts. -->
      <template v-if="isContent">
        <div class="notification-content-title">
          <span class="notification-content-glyph" aria-hidden="true">{{ sourceGlyph }}</span>
          <span class="fts-text-primary">{{ notification.title || 'Notification' }}</span>
        </div>
        <div class="notification-content-summary">{{ notification.message }}</div>
        <span class="notification-content-source" v-if="sourceLabel">{{ sourceLabel }}</span>
        <span class="notification-content-via" v-if="notification.actor && userProfileName && sourceLabel !== userProfileName">via {{ userProfileName }}</span>
      </template>
      <template v-else>
        <span class="fts-text-primary">{{ userProfileName }}</span>
        <span v-html="notification.shortMessage"></span>
         <fts-moji
          :size="15"
          :emoji="moduleData.emoji"
          class="notification-content-emoji"
          v-if="!!moduleData.emoji"
        />
        <span>{{ moduleData.name }}</span>
      </template>
      <fts-tippy
        v-if="notification.audit && notification.audit.updatedDate"
        :content="
          moment(notification.audit.updatedDate).format('llll')
        "
        :options="{ placement: 'right' }"
        trigger-class="notification-content-date"
      >
        <template #trigger>
          {{ momentRelativeTime(notification.audit.updatedDate) }}
        </template>
      </fts-tippy>
      <div
        class="notification-content-unread"
        v-if="!notification.read"
      ></div>
    </div>
  </fts-profile>
</template>

<script>
import moment from "moment";

import FTSProfile from "../FTSProfile";
import FTSMoji from "../FTSMoji";
import FTSTippy from "../FTSTippy";
import { clientStore } from '../../store/clientStore';
import commons from '../../commons/commons';

const SOURCE_GLYPHS = {
  routine: "⟳",
  teammate: "✦",
  automation: "⚙",
  mcp: "⇄",
  report: "▤",
  system: "ⓘ",
};

/** True for a locus-less Content notification (server Kind = NotificationMessageType.Content). */
export function isContentNotification(n) {
  return !!n && (n.kind === 2 || n.kind === "Content");
}

/**
 * Source kinds whose rows open INTO the GNey dock as a card when the dock is mounted
 * (docs/notification-content-proposal.md D11): composed content — GNey chat, routines, the
 * Notifications app from an automation or MCP. System notes (routine failed) and report
 * receipts keep the reader: their primary action is the routine panel / the Bench, not a chat.
 */
const DOCK_SOURCE_KINDS = ["teammate", "routine", "automation", "mcp"];

/** True when a Content row (without a subject entry) should open in the GNey dock. */
export function opensInGneyDock(n) {
  if (!isContentNotification(n)) return false;
  if (n.about && n.about.locusId) return false;
  const kind = n.source && n.source.kind ? String(n.source.kind).toLowerCase() : "";
  return DOCK_SOURCE_KINDS.indexOf(kind) >= 0;
}

/** Human source label for a Content notification: explicit label, else "GNey" / the kind. */
export function contentSourceLabel(n) {
  const s = n && n.source;
  if (!s) return "";
  if (s.label) return s.label;
  const kind = String(s.kind || "").toLowerCase();
  if (kind === "teammate") return "GNey";
  return kind ? kind.charAt(0).toUpperCase() + kind.slice(1) : "";
}

export default {
  components: {
    "fts-profile": FTSProfile,
    "fts-moji": FTSMoji,
    "fts-tippy": FTSTippy,
  },
  props: {
    notification: Object,
  },
  methods: {
    moment,
    momentRelativeTime(date) {
      return commons.momentRelativeTime(date);
    },
    select() {
      this.$emit('select', this.notification);
    },
  },
  computed: {
    isContent() {
      return isContentNotification(this.notification);
    },
    // Who the notification is *from*. `actor` is stamped in the sender's own
    // request when the notification is queued; the audit fields only say who
    // wrote the row, and the standard notifications are materialised later by
    // the unauthenticated ScheduledEventTrigger drain, which leaves them empty
    // (SS05991). Bot identities are not workspace members, so they resolve to
    // the fallback name rather than leaking the raw username.
    sender() {
      const audit = this.notification.audit || {};
      const sender = this.notification.actor || audit.createdBy || audit.updatedBy;

      if (!sender || ['inistate-bot', 'fetias-bot'].includes(sender.toLowerCase()))
        return null;

      return sender;
    },
    sourceLabel() {
      const s = this.notification && this.notification.source;
      if (!s) return "";
      if (s.label) return s.label;
      const kind = String(s.kind || "").toLowerCase();
      if (kind === "teammate") return "GNey";
      return kind ? kind.charAt(0).toUpperCase() + kind.slice(1) : "";
    },
    sourceGlyph() {
      const s = this.notification && this.notification.source;
      const kind = s ? String(s.kind || "").toLowerCase() : "";
      return SOURCE_GLYPHS[kind] || SOURCE_GLYPHS.system;
    },
    moduleData() {
      const module = (clientStore.state.workspaceModules || []).find(
        x => x.id == this.notification.vectorId
      ) || {};

      return {
        id: module.id,
        name: module.name,
        emoji: module.emoji,
      };
    },
  },
}
</script>

<style scoped>
.notification-content--rich .notification-content-title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: 600;
}
.notification-content-glyph {
  font-size: 0.875rem;
  line-height: 1;
  color: var(--fts-default, #6b7280);
}
.notification-content-summary {
  margin-top: 0.125rem;
  color: var(--fts-default, #6b7280);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.notification-content-source,
.notification-content-via {
  display: inline-block;
  margin-top: 0.25rem;
  margin-right: 0.375rem;
  font-size: 0.75rem;
  color: var(--fts-default, #6b7280);
}
.notification-content-source {
  padding: 0 0.5rem;
  border-radius: 999px;
  background: rgba(var(--fts-gray-100-rgb, 243, 244, 246), 0.9);
}
</style>
